import type { TypographyScaleKey } from 'geoview-core/ui/style/types';

/** Union type for all possible legend item types. */
export type TypeLegendItem = TypeLegendLayer | TypeHeaderLayer | TypeGroupLayer;

/**  A description item with text and collapsed states */
export interface TypeDescription {
  text: string;
  collapsed?: boolean;
}

/** A reference to an existing layer from geoview-core. */
export interface TypeLegendLayer {
  type: 'layer';
  layerPath: string;
}

/** A header text item for organizing legend sections. */
export interface TypeHeaderLayer {
  itemId?: string;
  type: 'header';
  text: string;
  description?: TypeDescription;
  /** Named size in the owning map's calibrated typography scale; takes precedence over fontSize. */
  fontSizeStep?: TypographyScaleKey;
  /** @deprecated Use fontSizeStep instead. Numeric values remain supported as exact design pixels. */
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
}

/** A collapsible group containing other legend items. */
export interface TypeGroupLayer {
  itemId?: string;
  type: 'group';
  text: string;
  description?: TypeDescription;
  collapsed?: boolean;
  children: TypeLegendItem[];
}

/** Main configuration for custom legend. */
export interface TypeCustomLegendConfig {
  isOpen: boolean;
  title: string;
  legendList: TypeLegendItem[];
  version?: string;
}

/**
 * Checks if the given item is a legend layer.
 *
 * @param item - The legend item to check
 * @returns true if the item is a legend layer
 */
export const isLegendLayer = (item: TypeLegendItem): item is TypeLegendLayer => {
  return item.type === 'layer';
};

/**
 * Checks if the given item is a header layer.
 *
 * @param item - The legend item to check
 * @returns true if the item is a header layer
 */
export const isHeaderLayer = (item: TypeLegendItem): item is TypeHeaderLayer => {
  return item.type === 'header';
};

/**
 * Checks if the given item is a group layer.
 *
 * @param item - The legend item to check
 * @returns true if the item is a group layer
 */
export const isGroupLayer = (item: TypeLegendItem): item is TypeGroupLayer => {
  return item.type === 'group';
};

/**
 * Generates a unique ID for a legend item based on its content and position.
 *
 * @param item - The legend item
 * @param index - The item's position in the list
 * @param parentPath - Optional parent path for nested items
 * @returns A unique identifier
 */
export function generateLegendItemId(item: TypeLegendItem, index: number, parentPath = ''): string {
  const basePath = parentPath ? `${parentPath}/` : '';

  if (isLegendLayer(item)) {
    // Use the layerPath as the unique ID
    return `${basePath}${item.layerPath}`;
  }

  if (isHeaderLayer(item)) {
    // Use itemId if provided, otherwise generate from text + index
    return item.itemId || `${basePath}header-${item.text.toLowerCase().replace(/\s+/g, '-')}-${index}`;
  }

  if (isGroupLayer(item)) {
    // Use itemId if provided, otherwise generate from title + index
    return item.itemId || `${basePath}group-${item.text.toLowerCase().replace(/\s+/g, '-')}-${index}`;
  }

  // Fallback
  return `${basePath}item-${index}`;
}
